import type {
  CampaignAbandonMotifDto,
  CampaignStateDto,
  CycleNumberDto,
  CycleStateDto,
  ParticipationStateDto,
  SeniorityLevelDto,
} from '@/api/campaign-client';

// =============================================================================
// campaign-labels.ts — Mapping enums campaign ASCII (API) → libellés FR + variant.
// =============================================================================
// L'API porte des enums ASCII (parité Prisma CampaignState / CycleState /
// SeniorityLevel / ParticipationState) ; l'UI les rend en libellés FR accentués
// (i18n front). WCAG 2.1 AA (ST-NFR-03) : la COULEUR n'est JAMAIS le seul porteur
// d'information — chaque état rend un LIBELLÉ TEXTE ; le variant de badge n'est qu'un
// renfort visuel.
//
// Aucune règle métier ici : table de traduction pure (valeur → libellé/variant).

/** Variant de badge shadcn disponible (cf. components/ui/badge.tsx). */
export type BadgeVariant = 'default' | 'secondary' | 'outline' | 'accent';

interface LabelWithVariant {
  readonly label: string;
  readonly variant: BadgeVariant;
}

/** 9 états campagne → libellés FR. */
const CAMPAIGN_STATE_LABELS: Record<CampaignStateDto, LabelWithVariant> = {
  Brouillon: { label: 'Brouillon', variant: 'outline' },
  Planifiee: { label: 'Planifiée', variant: 'secondary' },
  OuverteC1: { label: 'Ouverte (cycle 1)', variant: 'default' },
  AnalyseC1: { label: 'Analyse (cycle 1)', variant: 'accent' },
  OuverteC2: { label: 'Ouverte (cycle 2)', variant: 'default' },
  AnalyseC2: { label: 'Analyse (cycle 2)', variant: 'accent' },
  Negociation: { label: 'Négociation', variant: 'accent' },
  Publiee: { label: 'Publiée', variant: 'default' },
  Abandonnee: { label: 'Abandonnée', variant: 'outline' },
};

/** Libellé FR + variant d'un état de campagne. */
export function campaignStateLabel(state: CampaignStateDto): LabelWithVariant {
  return CAMPAIGN_STATE_LABELS[state];
}

// =============================================================================
// Filtre « statut opérationnel » de la liste campagnes (A-12, DBU §2.5 l.1068/1094).
// =============================================================================
// DBU ne nomme QUE le statut opérationnel comme filtre (« en cours, clôturée, brouillon,
// annulée ») ; les 9 états API sont regroupés en 4 BUCKETS métier. Mapping GATE-validé
// (scope-d3a-filtres-campagnes). Table de traduction PURE — aucune règle métier ici.

/** Bucket de statut opérationnel (DBU §2.5) + « tous » (défaut, pas de filtre). */
export type CampaignStatusBucket =
  | 'tous'
  | 'en-cours'
  | 'brouillon'
  | 'cloturee'
  | 'annulee';

/** Buckets sélectionnables (ordre d'affichage ; « tous » en tête = défaut). */
export const CAMPAIGN_STATUS_BUCKETS: readonly CampaignStatusBucket[] = [
  'tous',
  'en-cours',
  'brouillon',
  'cloturee',
  'annulee',
];

/** Libellé FR d'un bucket de statut opérationnel. */
const CAMPAIGN_STATUS_BUCKET_LABELS: Record<CampaignStatusBucket, string> = {
  tous: 'Tous les statuts',
  'en-cours': 'En cours',
  brouillon: 'Brouillon',
  cloturee: 'Clôturée',
  annulee: 'Annulée',
};

export function campaignStatusBucketLabel(bucket: CampaignStatusBucket): string {
  return CAMPAIGN_STATUS_BUCKET_LABELS[bucket];
}

/**
 * Mapping bucket → états API. GATE-validé : `en-cours` = cycles/analyse/négociation
 * ouverts, `brouillon` = pré-ouverture, `cloturee` = publiée, `annulee` = abandonnée.
 * `tous` n'a pas d'entrée (aucun filtrage).
 */
const BUCKET_TO_STATES: Record<
  Exclude<CampaignStatusBucket, 'tous'>,
  readonly CampaignStateDto[]
> = {
  'en-cours': [
    'OuverteC1',
    'AnalyseC1',
    'OuverteC2',
    'AnalyseC2',
    'Negociation',
  ],
  brouillon: ['Brouillon', 'Planifiee'],
  cloturee: ['Publiee'],
  annulee: ['Abandonnee'],
};

/**
 * Un état de campagne appartient-il au bucket ? `tous` accepte tout (aucun filtre).
 * Pur : sert le filtrage FRONT sur les données déjà chargées (contrat listCampaigns figé).
 */
export function campaignStateInBucket(
  state: CampaignStateDto,
  bucket: CampaignStatusBucket,
): boolean {
  if (bucket === 'tous') {
    return true;
  }
  return BUCKET_TO_STATES[bucket].includes(state);
}

/** Type guard : la valeur est-elle un bucket de statut connu ? (validation search param). */
export function isCampaignStatusBucket(
  value: unknown,
): value is CampaignStatusBucket {
  return (
    typeof value === 'string' &&
    (CAMPAIGN_STATUS_BUCKETS as readonly string[]).includes(value)
  );
}

/** 4 états de cycle → libellés FR. */
const CYCLE_STATE_LABELS: Record<CycleStateDto, LabelWithVariant> = {
  Pendante: { label: 'En attente', variant: 'outline' },
  Ouverte: { label: 'Ouvert', variant: 'default' },
  Cloturee: { label: 'Clôturé', variant: 'secondary' },
  Verrouillee: { label: 'Verrouillé', variant: 'accent' },
};

/** Libellé FR + variant d'un état de cycle. */
export function cycleStateLabel(state: CycleStateDto): LabelWithVariant {
  return CYCLE_STATE_LABELS[state];
}

/** 4 niveaux de séniorité → libellés FR. */
const SENIORITY_LABELS: Record<SeniorityLevelDto, string> = {
  Debutant: 'Débutant',
  Maitrisant: 'Maîtrisant',
  Expert: 'Expert',
  Referent: 'Référent',
};

/** Libellé FR d'un niveau de séniorité. */
export function seniorityLevelLabel(level: SeniorityLevelDto): string {
  return SENIORITY_LABELS[level];
}

/** Liste ordonnée des niveaux de séniorité (pour les sélecteurs). */
export const SENIORITY_LEVELS: readonly SeniorityLevelDto[] = [
  'Debutant',
  'Maitrisant',
  'Expert',
  'Referent',
];

/** 5 états de participation → libellés FR. */
const PARTICIPATION_STATE_LABELS: Record<
  ParticipationStateDto,
  LabelWithVariant
> = {
  Invite: { label: 'Invité', variant: 'outline' },
  EnSaisie: { label: 'En saisie', variant: 'secondary' },
  Soumis: { label: 'Soumis', variant: 'default' },
  Verrouille: { label: 'Verrouillé', variant: 'accent' },
  ReouvertNego: { label: 'Rouvert (négociation)', variant: 'accent' },
};

/** Libellé FR + variant d'un état de participation. */
export function participationStateLabel(
  state: ParticipationStateDto,
): LabelWithVariant {
  return PARTICIPATION_STATE_LABELS[state];
}

/** Libellé FR d'un numéro de cycle. */
export function cycleNumberLabel(cycleNumber: CycleNumberDto): string {
  return `Cycle ${String(cycleNumber)}`;
}

/** 4 motifs d'abandon (G0-S15-6) → libellés FR (i18n front ; parité enum CampaignAbandonMotif). */
const ABANDON_MOTIF_LABELS: Record<CampaignAbandonMotifDto, string> = {
  AnnulationClient: 'Annulation client',
  ErreurConfiguration: 'Erreur de configuration',
  Fusion: 'Fusion',
  Autre: 'Autre',
};

/** Libellé FR d'un motif d'abandon. */
export function abandonMotifLabel(motif: CampaignAbandonMotifDto): string {
  return ABANDON_MOTIF_LABELS[motif];
}

/** Liste ordonnée des motifs d'abandon (pour le sélecteur). */
export const ABANDON_MOTIFS: readonly CampaignAbandonMotifDto[] = [
  'AnnulationClient',
  'ErreurConfiguration',
  'Fusion',
  'Autre',
];
